import { Fragment } from "react";

import { type MarkdownBlock, isSafeHref, parseInline, parseMarkdownBlocks } from "../lib/markdown";

/**
 * Renders a dashboard markdown tile's source as real React nodes.
 *
 * Every node here is constructed, never injected: there is no
 * `dangerouslySetInnerHTML` on this path, so author-written content from
 * `recruiter-analytics` cannot introduce markup. That matters more than usual
 * because those definitions are ingested at RUNTIME (`BiRegistrySyncJob`), so this
 * content changes without a recruiter deploy or review.
 *
 * Headings map to `h2`-`h6`, never `h1`: the page already owns the single `h1`
 * (the dashboard title), and a tile authored with `#` should not compete with it
 * in the document outline. `#` and `##` therefore both render as `h2`, and deeper
 * levels shift down by one, bottoming out at `h6`.
 */
export function Markdown({ source, className }: { source: string; className?: string }) {
  const blocks = parseMarkdownBlocks(source);
  if (blocks.length === 0) return null;
  return (
    <div className={className ? `bi-markdown ${className}` : "bi-markdown"}>
      {blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: MarkdownBlock }) {
  switch (block.kind) {
    case "heading": {
      const Tag = `h${Math.min(block.level + 1, 6)}` as "h2" | "h3" | "h4" | "h5" | "h6";
      return (
        <Tag className={`bi-markdown__h bi-markdown__h--${block.level}`}>
          <Inline text={block.text} />
        </Tag>
      );
    }
    case "paragraph":
      return (
        <p className="bi-markdown__p">
          <Inline text={block.text} />
        </p>
      );
    case "list": {
      const Tag = block.ordered ? "ol" : "ul";
      return (
        <Tag className="bi-markdown__list">
          {block.items.map((item, i) => (
            <li key={i}>
              <Inline text={item} />
            </li>
          ))}
        </Tag>
      );
    }
    case "rule":
      return <hr className="bi-markdown__rule" />;
  }
}

function Inline({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((piece, i) => {
        let node = <>{piece.text}</>;
        if (piece.code) node = <code className="bi-markdown__code">{piece.text}</code>;
        if (piece.bold) node = <strong>{node}</strong>;
        if (piece.italic) node = <em>{node}</em>;
        // `isSafeHref` again, deliberately, even though `parseInline` already
        // refuses an unsafe scheme. This is the ONLY place in the app where an
        // authored string becomes a URL the browser will act on, so it does not
        // depend on a caller having used the sanitizing parser: any future path
        // that builds pieces another way is still guarded here. `rel` on every
        // outbound link, since this app reviews none of these hrefs.
        if (piece.href && isSafeHref(piece.href)) {
          node = (
            <a href={piece.href} target="_blank" rel="noreferrer noopener">
              {node}
            </a>
          );
        }
        return <Fragment key={i}>{node}</Fragment>;
      })}
    </>
  );
}
